import { formatStatusLabel } from '@/ui/primitives/statusChips';
import { metricsStatusOrder } from '@flowqa/shared';
import type {
  BuildStatus,
  FeatureDto,
  ReleaseDetailDto,
  SuiteExecutionDto,
  TestExecutionDto,
} from '@flowqa/shared';

export const featureStatusOptions: Array<{ value: FeatureDto['status']; label: string }> = [
  { value: 'draft', label: formatStatusLabel('draft') },
  { value: 'planned', label: formatStatusLabel('planned') },
  { value: 'ready', label: formatStatusLabel('ready') },
  { value: 'released', label: formatStatusLabel('released') },
];

export const releaseStatusOptions: Array<{ value: ReleaseDetailDto['status']; label: string }> = [
  { value: 'draft', label: formatStatusLabel('draft') },
  { value: 'planned', label: formatStatusLabel('planned') },
  { value: 'ready', label: formatStatusLabel('ready') },
  { value: 'released', label: formatStatusLabel('released') },
  { value: 'overdue', label: formatStatusLabel('overdue') },
];

export const buildStatusOptions: Array<{ value: BuildStatus; label: string }> = [
  { value: 'queued', label: formatStatusLabel('queued') },
  { value: 'running', label: formatStatusLabel('running') },
  { value: 'completed', label: formatStatusLabel('completed') },
  { value: 'failed', label: formatStatusLabel('failed') },
];

const suiteExecutionStatusOrder: Array<SuiteExecutionDto['status']> = [
  ...(['passed', 'failed', 'errored'] as const).filter((status) =>
    metricsStatusOrder.includes(status),
  ),
  'skipped',
];

export const suiteExecutionStatusOptions: Array<{
  value: SuiteExecutionDto['status'];
  label: string;
}> = suiteExecutionStatusOrder.map((status) => ({
  value: status,
  label: formatStatusLabel(status),
}));

const suiteExecutionSummaryStatusOrder: Array<SuiteExecutionDto['status']> = metricsStatusOrder;

export const suiteExecutionSummaryStatusOptions: Array<{
  value: SuiteExecutionDto['status'];
  label: string;
}> = suiteExecutionSummaryStatusOrder.map((status) => ({
  value: status,
  label: formatStatusLabel(status),
}));

const testExecutionStatusOrder: Array<TestExecutionDto['status']> = [
  ...(['passed', 'failed', 'errored', 'skipped'] as const).filter((status) =>
    metricsStatusOrder.includes(status),
  ),
];

export const testExecutionStatusOptions: Array<{
  value: TestExecutionDto['status'];
  label: string;
}> = testExecutionStatusOrder.map((status) => ({
  value: status,
  label: formatStatusLabel(status),
}));

const testExecutionSummaryStatusOrder: Array<TestExecutionDto['status']> = metricsStatusOrder;

export const testExecutionSummaryStatusOptions: Array<{
  value: TestExecutionDto['status'];
  label: string;
}> = testExecutionSummaryStatusOrder.map((status) => ({
  value: status,
  label: formatStatusLabel(status),
}));

export type ReleaseAutomationFlag =
  | 'automateReleaseStatuses'
  | 'requireFeaturesForReady'
  | 'requireReadyFeaturesForReady';

export const releaseAutomationLabels: Record<
  ReleaseAutomationFlag,
  { label: string; helperText: string }
> = {
  automateReleaseStatuses: {
    label: 'Automate Release Status',
    helperText:
      'Automatically derive release status from planned/released dates and feature readiness.',
  },
  requireFeaturesForReady: {
    label: 'Require Features',
    helperText: 'Releases cannot be marked as ready unless they include at least one feature.',
  },
  requireReadyFeaturesForReady: {
    label: 'Require All Features Ready',
    helperText:
      'When enabled, all linked features must be ready before the release can be marked as ready.',
  },
};
